import { useEffect, useState, useRef, useCallback } from "react"
import { parseAsArrayOf, parseAsString, useQueryState } from "nuqs"
import { toast } from "sonner"

import GroupCheckBox from "@/components/molecules/Groupcheckbox"
import AccordionContent from "@/components/atoms/Accordion/accordion-content"
import AccordionTrigger from "@/components/atoms/Accordion/accordion-tirgger"
import PlaceSearchInput from "@/components/molecules/PlaceSearch"
import { Accordion, AccordionItem } from "../Accordion/accordion"
import { cn } from '@/utils/index'
import { getCategoriesAction } from "@/actions/job/job.action"
import { JobType, ExperienceLevel } from "@/app/constants/data"
import { Category } from "@/app/types"
import { LocationData, usePlaceSearchStore } from "@/stores/places"
import useLocaleStore from "@/app/stores/useLocaleStore"

const WorkExperience = [
  {
    id: ExperienceLevel.JUNIOR,
    label: { en: "0 - 3 years", am: "0 - 3 ልምድ" },
  },
  {
    id: ExperienceLevel["MID-SENIOR"],
    label: { en: "3 - 6 years", am: "3 - 6 ልምድ" },
  },
  {
    id: ExperienceLevel.SENIOR,
    label: { en: "6 - 10 years", am: "6 - 10 ልምድ" },
  },
  {
    id: ExperienceLevel.EXPERT,
    label: { en: "More than 10 years", am: "ከ 10 አመት በላይ" },
  },
]

const jobTypeLabels = {
  [JobType["FULL-TIME"]]: { en: "Full Time", am: "ሙሉ ሰዓት" },
  [JobType["PART-TIME"]]: { en: "Part Time", am: "የትርፍ ሰዓት" },
  [JobType["CONTRACT"]]: { en: "Contract", am: "ኮንትራት" },
  [JobType["TEMPORARY"]]: { en: "Temporary", am: "ጊዜያዊ" },
  [JobType["INTERN"]]: { en: "Internship", am: "ተማሪ" },
  [JobType["VOLUNTEER"]]: { en: "Volunteer", am: "በፈቃደኝነት" }
}

export const JobListFilterForm = ({ filters, setFilters }: { filters: any; setFilters: (filters: any) => void }) => {
  const [categories, setCategories] = useState<Category[]>([])
  const [location, setLocation] = useState<LocationData | null>(null)

  const [selectedJobTypes, setSelectedJobTypes] = useQueryState(
    "type[]",
    parseAsArrayOf(parseAsString).withDefault([])
  )
  const [selectedroless, setSelectedroless] = useQueryState("roless[]", parseAsArrayOf(parseAsString).withDefault([]))
  const [selectedExperience, setSelectedExperience] = useQueryState(
    "experienceLevel[]",
    parseAsArrayOf(parseAsString).withDefault([])
  )

  const prevFiltersRef = useRef(filters)

  // Fetch categories only once
  useEffect(() => {
    getCategoriesAction()
      .then((response: any) => setCategories(response.data.data))
      .catch(() => toast.error("Failed to load categories."))
  }, [])

  const mappedJobTypes = selectedJobTypes
    .map((type) => {
      const key = type.toUpperCase().replace(/-/g, "-")
      return JobType[key as keyof typeof JobType]
    })
    .filter((type) => type !== undefined)

  const mappedExperienceLevels = selectedExperience
    .map((exp) => ExperienceLevel[exp.toUpperCase().replace(/-/g, "-") as keyof typeof ExperienceLevel])
    .filter((exp) => exp !== undefined)

  // Memoized function to update filters efficiently
  const updateFilters = useCallback(() => {
    const newFilters = {
      filters: {
        ...(selectedroless.length > 0 && { categories: selectedroless }),
        ...(mappedExperienceLevels.length > 0 && { experienceLevel: mappedExperienceLevels }),
        ...(mappedJobTypes.length > 0 && { type: mappedJobTypes }),
      },
      ...(location &&
        location.address !== "" && {
        location: {
          latitude: location.coordinates.lat,
          longitude: location.coordinates.lon,
          country: location.country,
          state: location.state,
          city: location.city
        },
      }),
    }

    if (JSON.stringify(newFilters) !== JSON.stringify(prevFiltersRef.current)) {
      prevFiltersRef.current = newFilters
      setFilters(newFilters)
    }
  }, [selectedroless, mappedExperienceLevels, mappedJobTypes, location, setFilters])

  // Run filter update only when necessary
  useEffect(() => {
    updateFilters()
  }, [selectedroless, mappedExperienceLevels, mappedJobTypes, location, updateFilters])

  const locale = useLocaleStore((state) => state.locale)
    ;

  // Reset filters efficiently
  const resetAllFilters = useCallback(() => {
    setSelectedJobTypes([])
    setSelectedroless([])
    setSelectedExperience([])
    setFilters({})
    usePlaceSearchStore.getState().clearLocationData()
    setLocation(null)

    const params = new URLSearchParams();
    params.set('page', '1');

  }, [setSelectedJobTypes, setSelectedroless, setSelectedExperience, setLocation, setFilters])

  return (
    <Accordion
      className={cn("min-w-96 max-w-96 animate-slideInLeft overflow-hidden lg:py-16")}
      type="multiple"
      defaultValue={["location", "type", "experience", "roles", "salary"]}
    >
      <div
        className="float-end mt-10 inline-flex cursor-pointer pb-5 text-[15px] text-black underline dark:text-gray-200 mr-7 lg:mr-0"
        onClick={resetAllFilters}
      >
        {locale === "am" ? "አጥፉ" : "Clear"}
      </div>

      {/* Location Filter */}
      <AccordionItem value="location">
        <AccordionTrigger className="bg-gradient-to-r from-blue-100 to-gray-100 p-5 text-base dark:bg-black dark:from-gray-950 dark:to-gray-800">
          {locale === "am" ? "አድራሻ" : "Location"}
        </AccordionTrigger>
        <AccordionContent className="relative z-50 px-2">
          <PlaceSearchInput
            borderless={false}
            value={location?.address ?? ""}
            onChange={(newLocation) => setLocation(newLocation as LocationData)}
            className="relative z-0"
          />
        </AccordionContent>
      </AccordionItem>

      {/* Job Types Filter */}
      <AccordionItem value="type">
        <AccordionTrigger className="bg-gradient-to-r from-blue-100 to-gray-100 p-5 text-base dark:bg-black dark:from-gray-950 dark:to-gray-800">
          {locale === "am" ? "የስራ አይነቶች" : "Job Types"}
        </AccordionTrigger>
        <AccordionContent className="px-2">
          <GroupCheckBox
            options={Object.entries(JobType).map(([_, value]) => ({
              id: value,
              label: locale === "am" ? jobTypeLabels[value].am : jobTypeLabels[value].en,
            }))}
            selectedOptions={selectedJobTypes}
            list={false}
            label={locale === "am" ? "የስራ አይነት" : "Job Type"}
            onChange={setSelectedJobTypes}
            dashboardInput={false}
          />
        </AccordionContent>
      </AccordionItem>

      {/* Job roless Filter */}
      <AccordionItem value="roles">
        <AccordionTrigger className="bg-gradient-to-r from-blue-100 to-gray-100 p-5 text-base dark:bg-black dark:from-gray-950 dark:to-gray-800">
          {locale === "am" ? "የስራ መደቦች" : "Job Roles"}
        </AccordionTrigger>
        <AccordionContent className="px-2">
          <GroupCheckBox
            options={categories.map((category) => ({
              id: category.id,
              label: category.name.charAt(0).toUpperCase() + category.name.slice(1),
            }))}
            selectedOptions={selectedroless}
            list={false}
            label={locale === "am" ? "የስራ መደቦች" : "Job Roles"}
            onChange={setSelectedroless}
            dashboardInput={false}
          />
        </AccordionContent>
      </AccordionItem>

      {/* Experience Filter */}
      <AccordionItem value="experience">
        <AccordionTrigger className="bg-gradient-to-r from-blue-100 to-gray-100 p-5 text-base dark:bg-black dark:from-gray-950 dark:to-gray-800">
          {locale === "am" ? "የሰራ ልምድ" : "Work Experience"}
        </AccordionTrigger>
        <AccordionContent className="px-2">
          <GroupCheckBox
            options={WorkExperience.map((item) => ({
              id: item.id,
              label: locale === "am" ? item.label.am : item.label.en,
            }))}
            selectedOptions={selectedExperience}
            list={false}
            label={locale === "am" ? "የሰራ ልምድ" : "Work Experience"}
            onChange={setSelectedExperience}
            dashboardInput={false}
          />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
